import { ModelName } from "../../../../types";
import type { TellescopeModelFromName } from "../../../../mappings";
import type { AccessEvaluator } from "../types";

type FilterAccessConstraint = { type: "filter"; field: string };
type CreatorOnlyConstraint = { type: "creatorOnly" };

/**
 * Subset of the schema's `AccessConstraint` understood by the common evaluator.
 * `dependency` constraints require model-specific logic and are intentionally
 * excluded — models that declare one must use a bespoke evaluator instead.
 */
export type CommonAccessConstraint = FilterAccessConstraint | CreatorOnlyConstraint;

/**
 * Builds the shared row-level access evaluator with its model's access
 * constraints bound at construction. Binding here (rather than as a per-call
 * parameter) makes it impossible to construct an evaluator without supplying
 * its constraints, removing a silent access-control footgun.
 *
 * Mirrors the precedence of the API's `modifyFilterForAccessConstraint` (the
 * source of truth):
 * 1. `creatorOnly` overrides every level, including `All` → creator only.
 * 2. `All` → granted (within the already-applied tenant scope).
 * 3. Otherwise access is the OR of:
 *    - each `filter` constraint: `record[field]` matches the user OR the user
 *      is the creator (applies at both `Default` and `Assigned`);
 *    - unconstrained `Default`: creator only;
 *    - `Assigned`: the record is linked to at least one *accessible* enduser
 *      (added to, not a replacement for, the filter/creator grants).
 * 4. `null`/unrecognized access type → denied.
 *
 * The "Assigned" grant is accurate to the backend's `record.enduserId ∈
 * {accessible endusers}` test: the hooks resolve which of the record's related
 * endusers the user can actually reach via the access-restricted
 * `endusers.getByIds` read, and pass that subset as
 * `accessibleRelatedEnduserIds`. Granting on any enduser-linked record would
 * over-grant (showing records that 403); granting only on `assignedTo` would
 * under-grant users with `All`/tag-based enduser access. The fetch's access
 * restriction is the accurate membership test, so neither approximation is used.
 */
export const makeCommonAccessEvaluator = (
  accessConstraints: readonly CommonAccessConstraint[]
): AccessEvaluator<ModelName> => {
  const getRelatedEnduserIds = (model: TellescopeModelFromName<ModelName>) => {
    const set = new Set<string>();
    if (typeof model === "object" && model !== null) {
      if ("enduserId" in model && model.enduserId) {
        set.add(model.enduserId);
      }
      if ("enduserIds" in model) {
        model.enduserIds?.forEach((id) => set.add(id));
      }
    }

    return set;
  };

  return {
    getRelatedEnduserIds,
    evaluate: ({ record, currentUserId, accessType, accessibleRelatedEnduserIds }) => {
      const creatorMatches = record.creator === currentUserId;

      if (accessConstraints.some((constraint) => constraint.type === "creatorOnly")) {
        return creatorMatches;
      }

      if (accessType === "All") {
        return true;
      }

      if (accessType !== "Default" && accessType !== "Assigned") {
        return false;
      }

      const filterConstraints = accessConstraints.filter(
        (constraint): constraint is FilterAccessConstraint => constraint.type === "filter"
      );

      const filterGrant = filterConstraints.some(({ field }) => {
        const value: unknown = Reflect.get(record, field);
        const fieldMatches = Array.isArray(value) ? value.includes(currentUserId) : value === currentUserId;
        return fieldMatches || creatorMatches;
      });

      const defaultCreatorGrant = filterConstraints.length === 0 && accessType === "Default" && creatorMatches;

      const assignedGrant = accessType === "Assigned" && accessibleRelatedEnduserIds.size > 0;

      return filterGrant || defaultCreatorGrant || assignedGrant;
    },
  };
};
